import { Component, OnInit, OnDestroy, HostListener, ViewChild, ChangeDetectionStrategy } from '@angular/core';


import { FormsModule } from '@angular/forms';
import { Subject, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConfirmLeaveDialogComponent, ConfirmLeaveResult } from 'src/app/modals/confirm-leave-dialog/confirm-leave-dialog.component';
import { TranslateModule } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { DashboardViewLookup, DashboardLayoutConfig, DEFAULT_LAYOUT } from 'src/app/models/dashboard-view.model';
import { DashboardViewWidget } from 'src/app/models/dashboard-view-widget.model';
import { DashboardWidgetType } from 'src/app/enums/dashboardWidgetType.enum';
import { DashboardViewService, DashboardViewWithWidgets } from 'src/app/services/dashboard-view.service';
import { DashboardViewWidgetService } from 'src/app/services/dashboard-view-widget.service';
import { DashboardUserViewService } from 'src/app/services/dashboard-user-view.service';
import { UserViewService } from 'src/app/services/user-view.service';
import { GridWidgetConfig } from 'src/app/models/dashboard';
import { OrganizationLevel } from 'src/app/models/organizationLevel.model';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from 'src/app/services/translate.service';
import { AuthService } from 'src/app/services';
import { CommonService } from 'src/app/services/common.service';
import { PermissionService } from 'src/app/services/permission.service';
import { DashboardOverrideService } from 'src/app/services/dashboard-override.service';
import { DeleteDialog } from 'src/app/shared/delete-dialog/delete-dialog.component';
import { DashboardViewSelectorComponent } from 'src/app/shared/dashboard/dashboard-view-selector/dashboard-view-selector.component';
import { DashboardSettingsFlyoutComponent } from 'src/app/shared/dashboard/dashboard-settings-flyout/dashboard-settings-flyout.component';
import { GRID_ENTITY_OPTIONS } from 'src/app/shared/dashboard/widget-picker-dialog/widget-picker-dialog.component';
import { DashboardWidgetsModule, getWidgetDefinition, getWidgetTypeFromString, getWidgetTypeString, WIDGET_TYPE_KEYS } from './dashboard-widgets/dashboard-widgets.module';
import { CommonComponentsModule } from 'src/app/common-components/common-components.module';
import { generateGUID } from 'src/app/_helpers/guid.helper';
import {
    GRID_DEFAULTS, RESIZE_DIRECTIONS, SETTINGS_TABS, CSS_SELECTORS,
    ResizeDirection, SettingsTab, RESIZE_MULTIPLIERS, MAX_WIDGETS
} from './dashboard.constants';
import { generateNextAvailableName } from 'src/app/shared/utils/view-name.util';
import {
    resolveWidgetTitle, getTitleSuffixIndex, isWidgetTitleCustom, preserveTitleConfig,
    withTitleSuffixIndex
} from './dashboard-widgets/widget-title.util';
import { EMPTY_WIDGET_TITLE_META, WidgetTitleMeta } from 'src/app/models/dashboard/widget-title.model';

const TEMP_WIDGET_ID_PREFIX = 'TEMP_';

interface GridSlot {
    index: number;
    x: number;
    y: number;
    widget: DashboardViewWidget | null;
    isOccupiedBySpan: boolean;
    spanOwnerWidget?: DashboardViewWidget;
}

@Component({
    standalone: true,
    selector: 'app-dashboard',
    imports: [
    FormsModule,
    TranslateModule,
    MatTooltipModule,
    MatSlideToggleModule,
    CommonComponentsModule,
    DashboardViewSelectorComponent,
    DashboardSettingsFlyoutComponent,
    DashboardWidgetsModule
],
    templateUrl: './dashboard.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {
    @ViewChild(DashboardViewSelectorComponent) viewSelector?: DashboardViewSelectorComponent;
    @ViewChild(DashboardSettingsFlyoutComponent) settingsFlyout?: DashboardSettingsFlyoutComponent;

    private readonly destroy$ = new Subject<void>();

    private layoutChanged = false;
    dirtyWidgetIds = new Set<string>();
    private deletedWidgetIds = new Set<string>();
    private viewPropertiesChanged = false;
    private originalWidgetsSnapshot?: DashboardViewWidget[];

    // Store original view properties when opening settings to detect changes
    private originalViewName?: string;
    private originalIsShared?: boolean;
    private originalOrgLevelId?: string;

    currentView?: DashboardViewWithWidgets;
    views: DashboardViewLookup[] = [];
    defaultViewId?: string;
    isLoading = true;
    organizationLevelId?: string;
    userFirstName: string;

    isRenamingView = false;
    renameInput = '';

    isSettingsOpen = false;
    settingsActiveTab: SettingsTab = SETTINGS_TABS.WIDGETS;
    selectedWidgetForSettings?: DashboardViewWidget;
    targetSlotIndex: number | null = null;

    gridSlots: GridSlot[] = [];
    widgetTitleByWidgetId: Record<string, string> = {};
    widgetTitleMetaByWidgetId: Record<string, WidgetTitleMeta> = {};
    readonly emptyTitleMeta = EMPTY_WIDGET_TITLE_META;

    isEditMode = false;
    isDragging = false;
    dragSourceSlotIndex: number | null = null;
    dragOverSlotIndex: number | null = null;

    isResizing = false;
    resizeWidget: DashboardViewWidget | null = null;
    resizeDirection: ResizeDirection = RESIZE_DIRECTIONS.SOUTHEAST;
    resizeStartX = 0;
    resizeStartY = 0;
    resizeStartWidth = 0;
    resizeStartHeight = 0;
    resizeStartPosX = 0;
    resizeStartPosY = 0;

    layoutConfig: DashboardLayoutConfig = { ...DEFAULT_LAYOUT };

    private _cachedView?: DashboardViewWithWidgets;
    private _cachedWidgetsRef?: DashboardViewWidget[];
    private _cachedVisibleWidgets: DashboardViewWidget[] = [];

    constructor(
        private dashboardViewService: DashboardViewService,
        private widgetService: DashboardViewWidgetService,
        private userViewService: DashboardUserViewService,
        private gridUserViewService: UserViewService,
        private toastService: ToastService,
        private translateService: TranslateService,
        private authService: AuthService,
        private commonService: CommonService,
        private permissionService: PermissionService,
        private dialog: MatDialog,
        private dashboardOverrideService: DashboardOverrideService
    ) {
        this.userFirstName = this.authService.getCurrentUser()?.userFullName?.split(' ')[0] ?? 'User';
        this.organizationLevelId = this.authService.getCurrentUser()?.organizationLevelIds?.[0];
    }

    ngOnInit(): void {
        this.initializeGridSlots();
        this.loadDefaultView();

        this.commonService.selectedOrgEmitter
            .pipe(takeUntil(this.destroy$))
            .subscribe((org: OrganizationLevel | null) => {
                if (org?.id && org.id !== this.organizationLevelId) {
                    this.organizationLevelId = org.id;
                }
            });

        this.dashboardOverrideService.lostAccessOnce$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.toastService.showInfo(
                this.translateService.getDynamicTranslation('Saved org filter no longer accessible. Using default.'), true
            ));
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.dashboardOverrideService.clearAll();
    }

    get gridColumns(): number {
        return this.layoutConfig?.columns ?? GRID_DEFAULTS.COLUMNS;
    }

    get gridRows(): number {
        const widgets = this.visibleWidgets;
        if (!widgets?.length) return GRID_DEFAULTS.ROWS;
        return Math.max(GRID_DEFAULTS.ROWS, Math.max(...widgets.map(w => w.positionY + (w.height || 1))));
    }

    get isDefaultView(): boolean {
        return !!this.currentView?.id && this.currentView.id === this.defaultViewId;
    }

    get canDeleteView(): boolean {
        return !this.isDefaultView && this.views.length > 1 && this.canManage;
    }

    get canManage(): boolean {
        return this.currentView?.canManage !== false;
    }

    get visibleWidgets(): DashboardViewWidget[] {
        const view = this.currentView;
        const widgets = view?.widgets;
        if (view !== this._cachedView || widgets !== this._cachedWidgetsRef) {
            this._cachedView = view;
            this._cachedWidgetsRef = widgets;
            this._cachedVisibleWidgets = widgets?.filter(
                w => w.visible && this.hasWidgetPermission(this.resolveWidgetTypeKey(w), w.configJson)
            ) ?? [];
        }
        return this._cachedVisibleWidgets;
    }

    private initializeGridSlots(): void {
        const { gridColumns: columns, gridRows: rows } = this;
        this.gridSlots = Array.from({ length: rows * columns }, (_, i) => ({
            index: i,
            x: i % columns,
            y: Math.floor(i / columns),
            widget: null,
            isOccupiedBySpan: false,
            spanOwnerWidget: undefined
        }));
    }

    private async loadDefaultView(): Promise<void> {
        this.isLoading = true;
        try {
            const view = await firstValueFrom(this.dashboardViewService.getDefaultView());
            if (view) {
                this.currentView = view;
                this.defaultViewId = view.id;
            } else {
                this.createLocalMockView();
                this.defaultViewId = undefined;
            }
        } catch {
            this.createLocalMockView();
            this.defaultViewId = undefined;
        } finally {
            this.syncLayoutAndGrid();
            this.isLoading = false;
        }
    }

    private syncLayoutAndGrid(): void {
        if (this.currentView?.layoutJson) {
            this.layoutConfig = { ...this.currentView.layoutJson };
        }
        if (this.gridSlots.length !== this.gridColumns * this.gridRows) {
            this.initializeGridSlots();
        }
        this.autoArrangeWidgets();
    }

    private computeWidgetTitles(): void {
        const titleMap: Record<string, string> = {};
        const metaMap: Record<string, WidgetTitleMeta> = {};
        (this.currentView?.widgets ?? []).forEach(w => {
            if (!w.id) return;
            const { widgetTitle, titleMeta } = resolveWidgetTitle(w, this.resolveWidgetTypeKey(w));
            titleMap[w.id] = widgetTitle;
            metaMap[w.id] = titleMeta;
        });
        this.widgetTitleByWidgetId = titleMap;
        this.widgetTitleMetaByWidgetId = metaMap;
    }

    private syncWidgetsToGrid(): void {
        this.computeWidgetTitles();
        this.gridSlots.forEach(slot => {
            slot.widget = null;
            slot.isOccupiedBySpan = false;
            slot.spanOwnerWidget = undefined;
        });

        const sortedWidgets = [...this.visibleWidgets].sort((a, b) => {
            if (this.isResizing && this.resizeWidget) {
                if (a.id === this.resizeWidget.id) return 1;
                if (b.id === this.resizeWidget.id) return -1;
            }
            return 0;
        });

        sortedWidgets.forEach(widget => {
            const { positionX: startX, positionY: startY, width = 1, height = 1 } = widget;
            const primaryIdx = startY * this.gridColumns + startX;

            if (primaryIdx >= 0 && primaryIdx < this.gridSlots.length) {
                this.gridSlots[primaryIdx].widget = widget;
                this.gridSlots[primaryIdx].isOccupiedBySpan = false;
                this.gridSlots[primaryIdx].spanOwnerWidget = undefined;
            }

            for (let dy = 0; dy < height; dy++) {
                for (let dx = 0; dx < width; dx++) {
                    if (dx === 0 && dy === 0) continue;
                    const spanX = startX + dx, spanY = startY + dy;
                    if (spanX < this.gridColumns && spanY < this.gridRows) {
                        const spanIdx = spanY * this.gridColumns + spanX;
                        if (spanIdx >= 0 && spanIdx < this.gridSlots.length) {
                            if (!this.gridSlots[spanIdx].widget) {
                                this.gridSlots[spanIdx].isOccupiedBySpan = true;
                                this.gridSlots[spanIdx].spanOwnerWidget = widget;
                            }
                        }
                    }
                }
            }
        });
    }

    private markLayoutChanged(): void {
        this.layoutChanged = true;
    }

    private markWidgetChanged(widgetId: string): void {
        this.dirtyWidgetIds.add(widgetId);
    }

    get hasPendingChanges(): boolean {
        return this.deletedWidgetIds.size > 0 || this.dirtyWidgetIds.size > 0 || this.layoutChanged || this.viewPropertiesChanged;
    }

    private clearChangeTracking(): void {
        this.layoutChanged = false;
        this.dirtyWidgetIds.clear();
        this.deletedWidgetIds.clear();
        this.viewPropertiesChanged = false;
        this.originalWidgetsSnapshot = undefined;
    }

    private storeOriginalViewProperties(): void {
        if (this.currentView) {
            this.originalViewName = this.currentView.viewName;
            this.originalIsShared = this.currentView.isShared;
            this.originalOrgLevelId = this.currentView.organizationLevelId;
        }
    }

    private createUnsavedView(options: {
        viewName: string;
        layoutJson?: DashboardLayoutConfig;
        widgets?: DashboardViewWidget[];
        isShared?: boolean;
        organizationLevelId?: string;
    }): DashboardViewWithWidgets {
        return {
            dashboardId: this.dashboardViewService.DASHBOARD_ID,
            viewName: options.viewName,
            layoutJson: options.layoutJson || { ...DEFAULT_LAYOUT },
            isShared: options.isShared ?? false,
            organizationLevelId: options.organizationLevelId ?? this.organizationLevelId,
            widgets: options.widgets || this.getDefaultWidgets()
        } as DashboardViewWithWidgets;
    }

    private createLocalMockView(): void {
        this.currentView = this.createUnsavedView({
            viewName: this.generateViewName(true)
        });
    }

    private buildViewPayload(viewName: string, widgets?: DashboardViewWidget[]): Partial<DashboardViewWithWidgets> {
        return {
            viewName,
            dashboardId: this.dashboardViewService.DASHBOARD_ID,
            layoutJson: this.currentView?.layoutJson || DEFAULT_LAYOUT,
            organizationLevelId: this.currentView?.organizationLevelId ?? this.organizationLevelId,
            isShared: this.currentView?.isShared ?? false,
            widgets: widgets?.map(w => ({ ...w, id: undefined })) || []
        };
    }

    private generateViewName(isInitial = false): string {
        const baseName = this.translateService.getDynamicTranslation(
            "{{firstName}}'s Dashboard",
            { firstName: this.userFirstName }
        );
        if (isInitial && this.views.length === 0) return baseName;
        return generateNextAvailableName(baseName, this.views.map(v => v.name));
    }

    private hasWidgetPermission(typeKey: string, configJson?: Record<string, unknown>): boolean {
        const def = getWidgetDefinition(typeKey);
        if (def?.requiredPermission && !this.permissionService.hasAllReadPermissions(def.requiredPermission)) {
            return false;
        }
        if (typeKey === getWidgetTypeString(DashboardWidgetType.GridView)) {
            if (!configJson) return false;
            const objectId = configJson[nameOf<GridWidgetConfig>(x => x.objectId)];
            if (typeof objectId !== 'string' || !objectId) return false;
            const entityOption = GRID_ENTITY_OPTIONS.find(e => e.objectId === objectId);
            if (!this.permissionService.hasReadPermission(objectId)) return false;
            if (entityOption?.dependsOnPermission && !this.permissionService.hasReadPermission(entityOption.dependsOnPermission)) {
                return false;
            }
        }
        return true;
    }

    private getDefaultWidgets(): DashboardViewWidget[] {
        const widget = (
            typeKey: string,
            positionX: number, positionY: number,
            width: number, height: number
        ): DashboardViewWidget => ({
            id: this.generateTempWidgetId(),
            dashboardViewId: this.currentView?.id,
            widgetType: getWidgetTypeFromString(typeKey),
            positionX, positionY, width, height,
            configJson: { widgetTypeKey: typeKey },
            visible: true
        });

        const allDefaults = [
            widget(WIDGET_TYPE_KEYS.SAMPLE_STATUS_PIPELINE,       0, 0, 4, 2),
            widget(WIDGET_TYPE_KEYS.EXCEEDANCE_COMPLIANCE,         0, 2, 2, 3),
            widget(WIDGET_TYPE_KEYS.SAMPLE_COLLECTION_METRICS,     2, 2, 2, 3),
            widget(WIDGET_TYPE_KEYS.SURVEY_PROGRESS,               0, 5, 2, 3),
            widget(WIDGET_TYPE_KEYS.EQUIPMENT_CALIBRATION_STATUS,  2, 5, 2, 3),
        ];

        return allDefaults.filter(w =>
            this.hasWidgetPermission(getWidgetTypeString(w.widgetType), w.configJson)
        );
    }

    private generateTempWidgetId(): string {
        return `${TEMP_WIDGET_ID_PREFIX}${generateGUID()}`;
    }

    isPersistedWidget(id: string): boolean {
        return !!id && !id.startsWith(TEMP_WIDGET_ID_PREFIX);
    }

    private showError(messageKey: string, persistent = false): void {
        this.toastService.displayErrorMessage(this.translateService.getDynamicTranslation(messageKey), persistent);
    }

    private showSuccess(messageKey: string, persistent = false): void {
        this.toastService.showSuccess(this.translateService.getDynamicTranslation(messageKey), persistent);
    }

    private async withLoading<T>(operation: () => Promise<T>): Promise<T | undefined> {
        this.isLoading = true;
        try {
            return await operation();
        } finally {
            this.isLoading = false;
        }
    }

    private async loadAndValidateView(viewId: string): Promise<DashboardViewWithWidgets | null> {
        const view = await firstValueFrom(this.dashboardViewService.getById(viewId));
        if (!view) {
            this.showError('View not found');
        }
        return view;
    }

    private getWidgetConstraints(widget: DashboardViewWidget) {
        const def = this.getWidgetDefinition(this.resolveWidgetTypeKey(widget));
        return {
            minWidth: def?.minWidth ?? 1,
            minHeight: def?.minHeight ?? 1,
            maxWidth: def?.maxWidth ?? this.gridColumns,
            maxHeight: def?.maxHeight ?? 100
        };
    }

    private generateUniqueName(baseName: string, existingNames: Set<string>, templateKey: string, indexPlaceholder = 'index'): string {
        if (!existingNames.has(baseName)) return baseName;
        let index = 1;
        let name: string;
        do {
            name = this.translateService.getDynamicTranslation(templateKey,
                templateKey.includes('index') ? { [indexPlaceholder]: ++index } : {});
        } while (existingNames.has(name));
        return name;
    }

    private findPositionInGrid(width: number, height: number, occupied: Map<string, string>, targetSlotIndex?: number | null): { x: number; y: number } {
        if (targetSlotIndex != null && targetSlotIndex >= 0) {
            const slot = this.gridSlots[targetSlotIndex];
            if (slot && this.canFitAt(slot.x, slot.y, width, height, occupied)) {
                return { x: slot.x, y: slot.y };
            }
        }

        for (let y = 0; y < GRID_DEFAULTS.MAX_SEARCH_ROWS; y++) {
            for (let x = 0; x <= this.gridColumns - width; x++) {
                if (this.canFitAt(x, y, width, height, occupied)) {
                    return { x, y };
                }
            }
        }

        return { x: 0, y: this.getMaxRowExtent() };
    }

    onDragStart(event: DragEvent, slotIndex: number): void {
        const slot = this.gridSlots[slotIndex];
        if (!slot.widget) return;
        this.isDragging = true;
        this.dragSourceSlotIndex = slotIndex;
        event.dataTransfer?.setData('text/plain', slotIndex.toString());
        if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
    }

    onDragOver(event: DragEvent, slotIndex: number): void {
        event.preventDefault();
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
        this.dragOverSlotIndex = slotIndex;
    }

    onDragLeave(): void {
        this.dragOverSlotIndex = null;
    }

    onDragEnd(): void {
        this.isDragging = false;
        this.dragSourceSlotIndex = null;
        this.dragOverSlotIndex = null;
    }

    onDrop(event: DragEvent, targetSlotIndex: number): void {
        event.preventDefault();
        if (this.dragSourceSlotIndex === null || this.dragSourceSlotIndex === targetSlotIndex) {
            this.onDragEnd();
            return;
        }

        const sourceSlot = this.gridSlots[this.dragSourceSlotIndex];
        const targetSlot = this.gridSlots[targetSlotIndex];
        if (!sourceSlot.widget) {
            this.onDragEnd();
            return;
        }

        const targetWidget = targetSlot.widget || targetSlot.spanOwnerWidget;
        if (targetWidget) {
            this.swapWidgets(sourceSlot.widget, targetWidget);
        } else {
            this.moveWidgetTo(sourceSlot.widget, targetSlot.x, targetSlot.y);
        }
        this.syncWidgetsToGrid();
        this.onDragEnd();
    }

    private swapWidgets(widgetA: DashboardViewWidget, widgetB: DashboardViewWidget): void {
        [widgetA.positionX, widgetB.positionX] = [widgetB.positionX, widgetA.positionX];
        [widgetA.positionY, widgetB.positionY] = [widgetB.positionY, widgetA.positionY];
        this.markWidgetChanged(widgetA.id);
        this.markWidgetChanged(widgetB.id);
        this.autoArrangeWidgets(widgetA.id);
    }

    private moveWidgetTo(widget: DashboardViewWidget, x: number, y: number): void {
        widget.positionX = x;
        widget.positionY = y;
        this.markWidgetChanged(widget.id);
        this.autoArrangeWidgets(widget.id);
    }

    private autoArrangeWidgets(priorityWidgetId?: string): void {
        const widgets = [...this.visibleWidgets].sort((a, b) => {
            if (a.id === priorityWidgetId) return -1;
            if (b.id === priorityWidgetId) return 1;
            return a.positionY !== b.positionY ? a.positionY - b.positionY : a.positionX - b.positionX;
        });

        const occupied = new Map<string, string>();

        for (const widget of widgets) {
            const { width = 1, height = 1 } = widget;

            if (widget.id === priorityWidgetId) {
                widget.positionX = Math.max(0, Math.min(widget.positionX, this.gridColumns - width));
                widget.positionY = Math.max(0, widget.positionY);
            }

            const hasOverlap = this.checkWidgetOverlap(widget, occupied) || widget.positionX + width > this.gridColumns;

            if (hasOverlap && widget.id !== priorityWidgetId) {
                const newPos = this.findAvailablePosition(widget, occupied);
                widget.positionX = newPos.x;
                widget.positionY = newPos.y;
            }

            for (let dy = 0; dy < height; dy++) {
                for (let dx = 0; dx < width; dx++) {
                    const cellX = widget.positionX + dx;
                    const cellY = widget.positionY + dy;
                    if (cellX < this.gridColumns) {
                        occupied.set(`${cellX},${cellY}`, widget.id);
                    }
                }
            }
        }

        this.initializeGridSlots();
        this.syncWidgetsToGrid();
    }

    private checkWidgetOverlap(widget: DashboardViewWidget, occupied: Map<string, string>): boolean {
        const { positionX: x, positionY: y, width = 1, height = 1 } = widget;
        for (let dy = 0; dy < height; dy++) {
            for (let dx = 0; dx < width; dx++) {
                if (occupied.has(`${x + dx},${y + dy}`)) return true;
            }
        }
        return false;
    }

    private findAvailablePosition(widget: DashboardViewWidget, occupied: Map<string, string>): { x: number; y: number } {
        const { width = 1, height = 1 } = widget;
        for (let y = 0; y < GRID_DEFAULTS.MAX_SEARCH_ROWS; y++) {
            for (let x = 0; x <= this.gridColumns - width; x++) {
                const canFit = !Array.from({ length: height * width }, (_, i) => {
                    const dx = i % width, dy = Math.floor(i / width);
                    return occupied.has(`${x + dx},${y + dy}`) && occupied.get(`${x + dx},${y + dy}`) !== widget.id;
                }).some(Boolean);
                if (canFit) return { x, y };
            }
        }
        return { x: 0, y: this.getMaxRowExtent() };
    }

    private getMaxRowExtent(): number {
        return this.visibleWidgets.length
            ? Math.max(...this.visibleWidgets.map(w => w.positionY + (w.height || 1)))
            : 0;
    }

    onViewsChanged(views: DashboardViewLookup[]): void {
        this.views = views;
    }

    async onViewSelected(event: { view: DashboardViewLookup; firstRender: boolean }): Promise<void> {
        const result = await this.confirmDiscardIfDirty();
        if (result === ConfirmLeaveResult.Stay) {
            this.viewSelector?.syncSelectedView();
            return;
        }
        this.loadViewById(event.view.id);
    }

    private async loadViewById(viewId: string): Promise<void> {
        await this.withLoading(async () => {
            try {
                const view = await firstValueFrom(this.dashboardViewService.getById(viewId));
                if (view) {
                    this.dashboardOverrideService.clearAll();
                    this.currentView = view;
                    this.syncLayoutAndGrid();
                }
            } catch {
                this.showError('Failed to load view', true);
            }
        });
    }

    async onAddView(_existingView?: DashboardViewWithWidgets | null): Promise<void> {
        const result = await this.confirmDiscardIfDirty();
        if (result === ConfirmLeaveResult.Stay) {
            return;
        }
        this.currentView = this.createUnsavedView({ viewName: this.generateViewName(false) });
        if (this.views.length === 0) {
            this.defaultViewId = undefined;
        }
        this.syncLayoutAndGrid();
    }

    onDefaultViewChanged(newDefaultViewId: string): void {
        this.defaultViewId = newDefaultViewId;
    }

    async onViewDuplicated(event: { originalView: DashboardViewLookup; newName: string }): Promise<void> {
        const result = await this.confirmDiscardIfDirty();
        if (result === ConfirmLeaveResult.Stay) {
            return;
        }
        await this.withLoading(async () => {
            try {
                const fullView = await this.loadAndValidateView(event.originalView.id);
                if (!fullView) return;

                this.currentView = this.createUnsavedView({
                    viewName: event.newName,
                    layoutJson: fullView.layoutJson,
                    widgets: fullView.widgets?.map(w => ({
                        ...w,
                        id: this.generateTempWidgetId(),
                        dashboardViewId: undefined
                    }))
                });

                this.syncLayoutAndGrid();
            } catch {
                this.showError('Failed to duplicate view');
            }
        });
    }

    startRenaming(): void {
        if (!this.currentView) return;
        this.renameInput = this.currentView.viewName;
        this.isRenamingView = true;
    }

    cancelRenaming(): void {
        this.isRenamingView = false;
        this.renameInput = '';
    }

    async confirmRename(): Promise<void> {
        const newName = this.renameInput.trim();
        if (!newName || !this.currentView || newName === this.currentView.viewName) {
            return this.cancelRenaming();
        }

        await this.withLoading(async () => {
            try {
                await firstValueFrom(this.dashboardViewService.rename(this.currentView!.id, newName));
                this.currentView!.viewName = newName;
                this.viewSelector?.fetchViews();
                this.showSuccess('View renamed successfully', true);
            } catch {
                this.showError('Failed to rename view');
            } finally {
                this.cancelRenaming();
            }
        });
    }

    async makeDefaultView(): Promise<void> {
        if (!this.currentView?.id || this.currentView.id === this.defaultViewId) return;

        await this.withLoading(async () => {
            try {
                await firstValueFrom(this.userViewService.saveDefaultView(this.currentView!.id));
                this.defaultViewId = this.currentView!.id;
                this.viewSelector?.fetchViews();
                this.showSuccess('Default view set successfully', true);
            } catch {
                this.showError('Failed to set default view');
            }
        });
    }

    async duplicateView(): Promise<void> {
        if (!this.currentView) return;

        await this.withLoading(async () => {
            try {
                const fullView = await this.loadAndValidateView(this.currentView!.id);
                if (!fullView) return;

                const baseName = this.translateService.getDynamicTranslation('{{viewName}} - Copy', { viewName: fullView.viewName });
                const existingNames = new Set(this.views.map(v => v.name));
                const newName = this.generateUniqueName(baseName, existingNames,
                    '{{viewName}} - Copy ({{index}})', 'index');

                const payload = this.buildViewPayload(newName, fullView.widgets);
                payload.layoutJson = fullView.layoutJson;
                const created = await firstValueFrom(this.dashboardViewService.create(payload as DashboardViewWithWidgets));

                this.currentView = created;
                this.viewSelector?.fetchViews();
                this.syncLayoutAndGrid();
                this.showSuccess('View duplicated successfully', true);
            } catch {
                this.showError('Failed to duplicate view');
            }
        });
    }

    deleteView(): void {
        if (!this.currentView || !this.canDeleteView) return;

        const dialogRef = this.dialog.open(DeleteDialog, {
            data: {
                title: 'View',
                service: this.dashboardViewService,
                customDelete: () => this.dashboardViewService.delete(this.currentView!.id)
            }
        });

        dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(async (confirmed: boolean) => {
            if (confirmed) {
                this.viewSelector?.fetchViews();
                await (this.defaultViewId ? this.loadViewById(this.defaultViewId) : this.loadDefaultView());
            }
        });
    }

    onEditView(): void {
        this.openSettings(SETTINGS_TABS.WIDGETS);
    }

    async toggleEditMode(): Promise<void> {
        if (this.isEditMode) {
            const result = await this.confirmDiscardIfDirty();
            if (result === ConfirmLeaveResult.Stay) {
                return;
            }
            this.isEditMode = false;
        } else {
            this.snapshotWidgets();
            this.isEditMode = true;
        }
    }

    openSettings(tab: SettingsTab = SETTINGS_TABS.WIDGETS): void {
        this.snapshotWidgets();
        this.storeOriginalViewProperties();
        this.settingsActiveTab = tab;
        this.selectedWidgetForSettings = undefined;
        this.targetSlotIndex = null;
        this.isSettingsOpen = true;
    }

    onEmptySlotClick(slot: GridSlot): void {
        this.snapshotWidgets();
        this.storeOriginalViewProperties();
        this.settingsActiveTab = SETTINGS_TABS.WIDGETS;
        this.selectedWidgetForSettings = undefined;
        this.targetSlotIndex = slot.index;
        this.isSettingsOpen = true;
    }

    openWidgetSettings(widget: DashboardViewWidget): void {
        this.snapshotWidgets();
        this.storeOriginalViewProperties();
        this.settingsActiveTab = SETTINGS_TABS.PROPERTIES;
        this.selectedWidgetForSettings = widget;
        this.targetSlotIndex = null;
        this.isSettingsOpen = true;
    }

    removeWidget(widget: DashboardViewWidget): void {
        if (!this.currentView) return;

        if (this.isPersistedWidget(widget.id)) {
            this.deletedWidgetIds.add(widget.id);
        }
        this.dirtyWidgetIds.delete(widget.id);

        this.currentView.widgets = this.currentView.widgets.filter(w => w.id !== widget.id);
        this.currentView = { ...this.currentView };

        this.syncWidgetsToGrid();
    }

    async closeSettings(updatedView: DashboardViewWithWidgets | null): Promise<void> {
        this.selectedWidgetForSettings = undefined;
        this.targetSlotIndex = null;

        if (!updatedView) {
            this.isSettingsOpen = false;
            this.originalWidgetsSnapshot = undefined;
            return;
        }

        this.isSettingsOpen = false;
        await this.persistChanges(updatedView);
    }

    private async persistChanges(updatedView: DashboardViewWithWidgets): Promise<void> {
        const nameChanged = this.originalViewName !== updatedView.viewName;
        const isSharedChanged = this.originalIsShared !== updatedView.isShared;
        const orgLevelChanged = this.originalOrgLevelId !== updatedView.organizationLevelId;

        if (nameChanged || isSharedChanged || orgLevelChanged) {
            this.viewPropertiesChanged = true;
        }

        this.currentView = updatedView;

        try {
            if (!this.currentView.id) {
                const payload = this.buildViewPayload(this.currentView.viewName, this.currentView.widgets);
                const created = await firstValueFrom(this.dashboardViewService.create(payload as DashboardViewWithWidgets));
                this.currentView = created;
                if (!this.defaultViewId) this.defaultViewId = created.id;
                this.showSuccess('View saved successfully', true);
            } else {
                const promises: Promise<unknown>[] = [];
                const widgetCreationPromises: { widget: DashboardViewWidget; promise: Promise<DashboardViewWidget> }[] = [];

                if (this.layoutChanged || this.viewPropertiesChanged) {
                    const existingWidgets = this.currentView.widgets.filter(w => this.isPersistedWidget(w.id));
                    const viewUpdatePayload = { ...this.currentView, widgets: existingWidgets };
                    promises.push(firstValueFrom(this.dashboardViewService.update(viewUpdatePayload as DashboardViewWithWidgets)));
                }

                for (const deletedId of this.deletedWidgetIds) {
                    promises.push(firstValueFrom(this.widgetService.delete(deletedId)));
                }

                for (const widget of this.currentView.widgets) {
                    const isNewWidget = !this.isPersistedWidget(widget.id);
                    if (isNewWidget && widget.visible) {
                        widgetCreationPromises.push({
                            widget,
                            promise: firstValueFrom(this.widgetService.create({ ...widget, id: undefined }))
                        });
                    } else if (!isNewWidget && this.dirtyWidgetIds.has(widget.id)) {
                        promises.push(firstValueFrom(this.widgetService.update(widget)));
                    }
                }

                const hasChanges = promises.length > 0 || widgetCreationPromises.length > 0 || this.deletedWidgetIds.size > 0;
                if (hasChanges) {
                    await Promise.all(promises);

                    if (widgetCreationPromises.length > 0) {
                        const createdWidgets = await Promise.all(widgetCreationPromises.map(w => w.promise));

                        const userViewPromises: Promise<unknown>[] = [];
                        for (let i = 0; i < createdWidgets.length; i++) {
                            const createdWidget = createdWidgets[i];
                            const originalWidget = widgetCreationPromises[i].widget;

                            if (originalWidget.widgetType === DashboardWidgetType.GridView) {
                                const gridConfig = originalWidget.configJson as GridWidgetConfig;
                                if (gridConfig?.gridViewId) {
                                    userViewPromises.push(
                                        firstValueFrom(this.gridUserViewService.saveDefaultView({
                                            gridId: `WID_${createdWidget.id}`,
                                            defaultGridViewId: gridConfig.gridViewId
                                        })).catch(() => {})
                                    );
                                }
                            }
                        }

                        if (userViewPromises.length > 0) {
                            await Promise.all(userViewPromises);
                        }
                    }

                    this.currentView = await firstValueFrom(this.dashboardViewService.getById(this.currentView.id));
                    this.showSuccess('View updated successfully', true);
                }
            }
            this.viewSelector?.fetchViews();
            this.clearChangeTracking();
        } catch (err) {
            this.toastService.showError(err, this.translateService.getDynamicTranslation('Failed to save view'));
        }

        this.syncLayoutAndGrid();
    }

    onWidgetsChanged(event: { widgets: DashboardViewWidget[], changedWidgetId?: string }): void {
        if (!this.currentView) return;

        if (event.changedWidgetId) {
            const changedWidget = event.widgets.find(w => w.id === event.changedWidgetId);
            if (changedWidget?.visible) {
                const position = this.findAvailablePositionForExistingWidget(changedWidget, event.widgets);
                changedWidget.positionX = position.x;
                changedWidget.positionY = position.y;
            }
            this.markWidgetChanged(event.changedWidgetId);
        }

        this.currentView.widgets = event.widgets;
        this.autoArrangeWidgets(event.changedWidgetId);
        this.syncWidgetsToGrid();
    }

    onLayoutChanged(layout: DashboardLayoutConfig): void {
        if (!this.currentView) return;
        const columnsChanged = this.layoutConfig.columns !== layout.columns;
        this.currentView.layoutJson = layout;
        this.layoutConfig = { ...layout };
        this.markLayoutChanged();
        if (columnsChanged) {
            this.autoArrangeWidgets();
            this.initializeGridSlots();
            this.syncWidgetsToGrid();
        }
    }

    private readGridTarget(configJson: Record<string, unknown> | undefined): { objectId?: string; viewName?: string } {
        const objectId = configJson?.[nameOf<GridWidgetConfig>(x => x.objectId)];
        const viewName = configJson?.[nameOf<GridWidgetConfig>(x => x.viewName)];
        return {
            objectId: typeof objectId === 'string' && objectId ? objectId : undefined,
            viewName: typeof viewName === 'string' && viewName ? viewName : undefined
        };
    }

    private isSameGridTarget(existingConfig: Record<string, unknown> | undefined, newConfig: Record<string, unknown>): boolean {
        const existing = this.readGridTarget(existingConfig);
        const incoming = this.readGridTarget(newConfig);
        return existing.objectId === incoming.objectId && existing.viewName === incoming.viewName;
    }

    onWidgetAdded(event: { widgetType: string; position?: { x: number; y: number; width: number; height: number }; targetSlotIndex?: number | null; configJson?: string }): void {
        if (!this.currentView) return;
        const definition = getWidgetDefinition(event.widgetType);
        if (!definition) return;

        const visibleWidgetCount = this.currentView.widgets.filter(w => w.visible).length;
        if (visibleWidgetCount >= MAX_WIDGETS) {
            this.showError('Maximum number of widgets reached ({{maxWidgets}}). Please remove some widgets first.');
            return;
        }

        const widgetWidth = definition.defaultWidth;
        const widgetHeight = definition.defaultHeight;

        let targetSlot = this.findAvailablePositionForNewWidget(
            widgetWidth,
            widgetHeight,
            event.targetSlotIndex
        );

        const widgetTypeEnum = getWidgetTypeFromString(event.widgetType);

        let widgetConfig: Record<string, unknown> = {};
        if (event.configJson) {
            try {
                widgetConfig = JSON.parse(event.configJson);
            } catch {
                widgetConfig = {};
            }
        } else {
            widgetConfig = Object.fromEntries(definition.configSchema.map(f => [f.key, f.default]));
        }

        const sameKindWidgets = this.currentView.widgets.filter(w =>
            this.resolveWidgetTypeKey(w) === event.widgetType && !isWidgetTitleCustom(w.configJson)
        );
        const matchingWidgets = event.widgetType === WIDGET_TYPE_KEYS.GRID_VIEW
            ? sameKindWidgets.filter(w => this.isSameGridTarget(w.configJson, widgetConfig))
            : sameKindWidgets;

        const titleSuffixIndex = matchingWidgets.length === 0
            ? undefined
            : Math.max(
                matchingWidgets.length,
                ...matchingWidgets.map(w => getTitleSuffixIndex(w, event.widgetType) ?? 1)
            ) + 1;

        const newWidget = {
            id: this.generateTempWidgetId(),
            dashboardViewId: this.currentView.id,
            widgetType: widgetTypeEnum,
            positionX: targetSlot.x,
            positionY: targetSlot.y,
            width: widgetWidth,
            height: widgetHeight,
            configJson: withTitleSuffixIndex({ ...widgetConfig, widgetTypeKey: event.widgetType }, titleSuffixIndex),
            visible: true
        };

        this.currentView.widgets = [...this.currentView.widgets, newWidget];
        this.currentView = { ...this.currentView };
        this.initializeGridSlots();
        this.syncWidgetsToGrid();
        this.targetSlotIndex = null;
    }

    private buildOccupiedCellsMap(widgets: DashboardViewWidget[]): Map<string, string> {
        const occupied = new Map<string, string>();
        for (const widget of widgets) {
            const w = widget.width || 1;
            const h = widget.height || 1;
            for (let dy = 0; dy < h; dy++) {
                for (let dx = 0; dx < w; dx++) {
                    occupied.set(`${widget.positionX + dx},${widget.positionY + dy}`, widget.id);
                }
            }
        }
        return occupied;
    }

    private findAvailablePositionForNewWidget(width: number, height: number, targetSlotIndex?: number | null): { x: number; y: number } {
        const occupied = this.buildOccupiedCellsMap(this.visibleWidgets);
        return this.findPositionInGrid(width, height, occupied, targetSlotIndex);
    }

    private canFitAt(x: number, y: number, width: number, height: number, occupied: Map<string, string>): boolean {
        if (x + width > this.gridColumns) return false;
        for (let dy = 0; dy < height; dy++) {
            for (let dx = 0; dx < width; dx++) {
                if (occupied.has(`${x + dx},${y + dy}`)) return false;
            }
        }
        return true;
    }

    private findAvailablePositionForExistingWidget(widget: DashboardViewWidget, allWidgets: DashboardViewWidget[]): { x: number; y: number } {
        const width = widget.width || 1;
        const height = widget.height || 1;
        const widgetsToMap = allWidgets.filter(w => w.visible && w.id !== widget.id);
        const occupied = this.buildOccupiedCellsMap(widgetsToMap);
        return this.findPositionInGrid(width, height, occupied);
    }

    onWidgetRemoved(widgetId: string): void {
        if (!this.currentView) return;

        const widgetIndex = this.currentView.widgets.findIndex(w => w.id === widgetId);
        if (widgetIndex === -1) return;

        const widget = this.currentView.widgets[widgetIndex];
        if (this.isPersistedWidget(widget.id)) this.deletedWidgetIds.add(widget.id);
        this.dirtyWidgetIds.delete(widget.id);
        this.dashboardOverrideService.clearWidget(widget.id);
        this.currentView.widgets.splice(widgetIndex, 1);
        this.currentView = { ...this.currentView };
        this.syncWidgetsToGrid();
    }

    onWidgetConfigured(event: { widgetId: string; config: Record<string, unknown> }): void {
        const widget = this.currentView?.widgets.find(w => w.id === event.widgetId);
        if (widget) {
            const widgetTypeKey = this.resolveWidgetTypeKey(widget);
            widget.configJson = preserveTitleConfig(widget.configJson, { ...event.config, widgetTypeKey: widgetTypeKey });
            this.markWidgetChanged(widget.id);
            this.computeWidgetTitles();
        }
    }

    onWidgetTitleChanged(widgetId: string): void {
        this.markWidgetChanged(widgetId);
        this.computeWidgetTitles();
    }

    async saveWidget(widget: DashboardViewWidget): Promise<void> {
        if (!widget || !this.isPersistedWidget(widget.id)) return;
        try {
            await firstValueFrom(this.widgetService.update(widget));
            this.dirtyWidgetIds.delete(widget.id);
            this.showSuccess('Widget saved', false);
        } catch {
            this.showError('Failed to save widget');
        }
    }

    onResetToDefault(): void {
        if (!this.currentView) return;
        this.currentView.widgets.forEach(w => { if (this.isPersistedWidget(w.id)) this.deletedWidgetIds.add(w.id); });
        this.dirtyWidgetIds.clear();
        this.currentView.widgets = this.getDefaultWidgets();
        this.currentView.layoutJson = { ...DEFAULT_LAYOUT };
        this.layoutChanged = true;
        this.currentView = { ...this.currentView };
        this.syncWidgetsToGrid();
    }

    getWidgetDefinition(widgetType: string) {
        return getWidgetDefinition(widgetType);
    }

    getWidgetTypeString(widgetType: DashboardWidgetType): string {
        return getWidgetTypeString(widgetType);
    }

    resolveWidgetTypeKey(widget: DashboardViewWidget): string {
        return widget.configJson?.widgetTypeKey || widget.configJson?._widgetTypeKey || getWidgetTypeString(widget.widgetType);
    }

    onResizeStart(event: MouseEvent, widget: DashboardViewWidget, direction: ResizeDirection = RESIZE_DIRECTIONS.SOUTHEAST): void {
        event.preventDefault();
        event.stopPropagation();
        this.isResizing = true;
        this.resizeWidget = widget;
        this.resizeDirection = direction;
        this.resizeStartX = event.clientX;
        this.resizeStartY = event.clientY;
        this.resizeStartWidth = widget.width || GRID_DEFAULTS.MIN_WIDGET_SIZE;
        this.resizeStartHeight = widget.height || GRID_DEFAULTS.MIN_WIDGET_SIZE;
        this.resizeStartPosX = widget.positionX;
        this.resizeStartPosY = widget.positionY;
    }

    @HostListener('document:mousemove', ['$event'])
    onResizeMove(event: MouseEvent): void {
        if (!this.isResizing || !this.resizeWidget) return;

        const gridElement = document.querySelector(CSS_SELECTORS.DASHBOARD_GRID) as HTMLElement;
        if (!gridElement) return;

        const cellWidth = gridElement.clientWidth / this.gridColumns;
        const rowHeightPx = (this.layoutConfig.rowHeight / 100) * window.innerHeight;
        const cellHeight = rowHeightPx + this.layoutConfig.gap;
        const deltaX = event.clientX - this.resizeStartX;
        const deltaY = event.clientY - this.resizeStartY;

        const { minWidth, minHeight, maxWidth, maxHeight } = this.getWidgetConstraints(this.resizeWidget);
        const mult = RESIZE_MULTIPLIERS[this.resizeDirection];

        // Calculate raw new values
        let newWidth = Math.round(this.resizeStartWidth + (deltaX * mult.width) / cellWidth);
        let newHeight = Math.round(this.resizeStartHeight + (deltaY * mult.height) / cellHeight);
        let newX = this.resizeStartPosX + Math.round((deltaX * mult.x) / cellWidth);
        let newY = this.resizeStartPosY + Math.round((deltaY * mult.y) / cellHeight);

        if (newX < 0) {
            newWidth += newX;
            newX = 0;
        }

        if (newY < 0) {
            newHeight += newY;
            newY = 0;
        }

        newWidth = Math.max(minWidth, Math.min(newWidth, maxWidth));
        newHeight = Math.max(minHeight, Math.min(newHeight, maxHeight));

        if (newX + newWidth > this.gridColumns) {
            if (mult.x > 0) {
                newX = this.gridColumns - newWidth;
            } else {
                newWidth = this.gridColumns - newX;
            }
        }

        newWidth = Math.max(minWidth, newWidth);
        newHeight = Math.max(minHeight, newHeight);
        newX = Math.max(0, newX);
        newY = Math.max(0, newY);

        Object.assign(this.resizeWidget, { positionX: newX, positionY: newY, width: newWidth, height: newHeight });
        this.syncWidgetsToGrid();
    }

    @HostListener('document:mouseup')
    onResizeEnd(): void {
        if (this.isResizing && this.resizeWidget) {
            const { minWidth, minHeight } = this.getWidgetConstraints(this.resizeWidget);

            this.resizeWidget.width = Math.max(minWidth, this.resizeWidget.width || 1);
            this.resizeWidget.height = Math.max(minHeight, this.resizeWidget.height || 1);

            if (this.resizeWidget.positionX + this.resizeWidget.width > this.gridColumns) {
                this.resizeWidget.width = this.gridColumns - this.resizeWidget.positionX;
            }

            this.autoArrangeWidgets(this.resizeWidget.id);
            this.markWidgetChanged(this.resizeWidget.id);
            this.isResizing = false;
            this.resizeWidget = null;
        }
    }

    resizeWidgetTo(widget: DashboardViewWidget, width: number, height: number): void {
        widget.width = width;
        widget.height = height;
        this.markWidgetChanged(widget.id);
        this.autoArrangeWidgets(widget.id);
        this.syncWidgetsToGrid();
    }

    private snapshotWidgets(): void {
        if (this.originalWidgetsSnapshot) return;
        this.originalWidgetsSnapshot = this.currentView?.widgets?.map(w => structuredClone(w));
    }

    private restoreWidgetSnapshot(): void {
        if (this.currentView && this.originalWidgetsSnapshot) {
            this.currentView.widgets = this.originalWidgetsSnapshot.map(w => structuredClone(w));
            this.currentView = { ...this.currentView };
            this.originalWidgetsSnapshot = undefined;
            this.syncLayoutAndGrid();
        }
    }

    private confirmInFlight?: Promise<ConfirmLeaveResult>;

    private confirmDiscardIfDirty(): Promise<ConfirmLeaveResult> {
        if (!this.hasPendingChanges) return Promise.resolve(ConfirmLeaveResult.Discard);
        if (this.confirmInFlight) return this.confirmInFlight;
        const ref = this.dialog.open(ConfirmLeaveDialogComponent, {
            width: '400px',
            disableClose: true,
            data: { showSave: true }
        });
        this.confirmInFlight = firstValueFrom(ref.afterClosed()).then(async (result?: ConfirmLeaveResult) => {
            const choice: ConfirmLeaveResult = result ?? ConfirmLeaveResult.Stay;
            if (choice === ConfirmLeaveResult.Discard) {
                this.restoreWidgetSnapshot();
                this.clearChangeTracking();
            } else if (choice === ConfirmLeaveResult.Save) {
                const view = this.settingsFlyout?.applyPendingState() ?? this.currentView;
                if (view) {
                    await this.persistChanges(view);
                }
            }
            return choice;
        }).finally(() => {
            this.confirmInFlight = undefined;
        });
        return this.confirmInFlight;
    }

    trackBySlotIndex(_index: number, slot: GridSlot): string {
        return slot.widget ? `widget-${slot.widget.id}` : `slot-${slot.index}`;
    }

    trackByWidgetId(_index: number, widget: DashboardViewWidget): string {
        return widget.id;
    }
}